import { useState, useRef } from 'react';
import { Download, Upload, Check, AlertCircle } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  exportScene,
  downloadSceneJSON,
  importScene,
  readJSONFile,
} from '../utils/sceneExport';
import { useSceneStore } from '../stores/sceneStore';
import { ValidationErrorDisplay } from './dialogs/ValidationErrorDisplay';
import type { ValidationError } from '../schemas/sceneSchema';

/**
 * Component for scene export and import functionality
 * Provides buttons to export scene to JSON and import from file
 */
export function SceneExportImport() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    []
  );
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const isDirty = useSceneStore((state) => state.isDirty);
  const markClean = useSceneStore((state) => state.markClean);

  /**
   * Handle export button click
   * Exports scene to JSON and triggers download
   */
  const handleExport = () => {
    try {
      const sceneData = exportScene();
      downloadSceneJSON(sceneData);
      markClean();

      // Show success message
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Export failed:', error);
      alert(
        `Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  /**
   * Handle import button click
   * Opens file picker
   */
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  /**
   * Handle file selection
   * Validates file and shows confirmation if needed
   */
  const handleFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if there are unsaved changes
    if (isDirty) {
      setPendingFile(file);
      setShowConfirmation(true);
      return;
    }

    // No unsaved changes, proceed with import
    await processImport(file);

    // Reset file input
    event.target.value = '';
  };

  /**
   * Process file import
   * Reads file, validates, and loads into store
   */
  const processImport = async (file: File) => {
    try {
      // Read JSON file
      const jsonData = await readJSONFile(file);

      // Validate and import
      const result = importScene(jsonData);

      if (!result.success) {
        // Show validation errors
        setValidationErrors(result.errors || []);
        setShowValidationErrors(true);
        return;
      }

      // Success - mark as clean since we just loaded a scene
      markClean();

      // Show success message
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Import failed:', error);
      alert(
        `Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  /**
   * Handle confirmation to proceed with import
   */
  const handleConfirmImport = async () => {
    setShowConfirmation(false);

    if (pendingFile) {
      await processImport(pendingFile);
      setPendingFile(null);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Handle cancellation of import
   */
  const handleCancelImport = () => {
    setShowConfirmation(false);
    setPendingFile(null);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      {/* Export/Import Buttons */}
      <div className="flex items-center gap-2">
        {/* Export Button */}
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg
                   bg-dark-border hover:bg-dark-border/80
                   text-dark-text text-sm font-medium
                   transition-colors"
          title="Export scene to JSON"
        >
          <Download className="w-4 h-4" />
          <span>Export</span>
        </button>

        {/* Import Button */}
        <button
          onClick={handleImportClick}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg
                   bg-dark-border hover:bg-dark-border/80
                   text-dark-text text-sm font-medium
                   transition-colors"
          title="Import scene from JSON"
        >
          <Upload className="w-4 h-4" />
          <span>Import</span>
        </button>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileSelected}
          className="hidden"
        />

        {/* Success Indicator */}
        {showSuccess && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/20">
            <Check className="w-4 h-4 text-green-400" />
            <span className="text-sm text-green-400">Success!</span>
          </div>
        )}
      </div>

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog.Root open={showConfirmation} onOpenChange={setShowConfirmation}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2
                     bg-dark-panel border panel-border rounded-lg shadow-xl
                     max-w-md w-full z-50"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-6 py-4 border-b panel-border">
              <div className="p-2 rounded-lg bg-yellow-500/10">
                <AlertCircle className="w-5 h-5 text-yellow-400" />
              </div>
              <Dialog.Title className="text-lg font-semibold text-dark-text">
                Unsaved Changes
              </Dialog.Title>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <p className="text-sm text-dark-muted">
                You have unsaved changes in your current scene. Importing a new
                scene will replace all current data. Are you sure you want to
                continue?
              </p>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t panel-border">
              <button
                onClick={handleCancelImport}
                className="px-4 py-2 rounded-lg hover:bg-dark-border
                         text-dark-text text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmImport}
                className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600
                         text-white text-sm font-medium transition-colors"
              >
                Import Anyway
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Validation Errors Dialog */}
      <ValidationErrorDisplay
        isOpen={showValidationErrors}
        onClose={() => setShowValidationErrors(false)}
        errors={validationErrors}
        title="Scene Import Errors"
      />
    </>
  );
}
